import { useMemo, ReactNode, SyntheticEvent, useCallback, useEffect } from 'react';
import { Tabs, Tab, Box, Stack } from '@mui/material';
import { TabsRenderingStrategy } from '@/types';
import { abyssColors } from '@/theme/colors';

type TabPanelProps = {
  children?: ReactNode;
  index: number;
  value: number;
  renderingStrategy?: TabsRenderingStrategy;
};

export type TabType = {
  disabled?: boolean | undefined;
  index: number;
  label: string | ReactNode;
  component: ReactNode;
  testid?: string;
};

type BasicTabsProps = {
  tabs: TabType[];
  selectedTab: number;
  changeTab: (selectedTabIndex: number) => void;
  fullWidth?: boolean;
  variant?: 'pills' | 'default';
  tabsStyles?: Object;
  tabsListRightMargin?: string;
  hidden?: boolean;
  tabsBackgroundColor?: string;
  handleResetFilters?: () => void;
  renderingStrategy?: TabsRenderingStrategy;
  customSeperator?: ReactNode | undefined;
};

function TabPanel(props: TabPanelProps) {
  const { children, value, index, renderingStrategy, ...other } = props;

  const renderedChildren: React.ReactNode = useMemo(() => {
    // If the strategy is eager, keep all the children rendered
    // but set the display to none for the ones that are not selected
    // this ensures, the components are mounted and their state is not lost
    if (renderingStrategy === 'eager')
      return <div style={{ display: value === index ? 'block' : 'none' }}>{children}</div>;

    // in case renderingStrategy is lazy, or not provided, load only the selected tab
    // any other tab will not be rendered, and once a tab is unseen, it will be unmounted
    // and all its state will be lost
    return value === index ? children : <></>;
  }, [renderingStrategy, children, value, index]);

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      style={{ height: '100%' }}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {renderedChildren}
    </div>
  );
}

export function BasicTabs({
  tabs,
  selectedTab,
  changeTab,
  fullWidth = true,
  variant = 'default',
  tabsStyles = {},
  tabsListRightMargin = 'inherit',
  hidden = false,
  tabsBackgroundColor = 'inherit',
  handleResetFilters,
  renderingStrategy = 'lazy',
  customSeperator,
}: BasicTabsProps) {
  const handleChange = (event: SyntheticEvent, newValue: number) => {
    changeTab(newValue);
    if (handleResetFilters) handleResetFilters();
  };

  // Automatically switch to first enabled tab if current tab becomes disabled
  useEffect(() => {
    const currentTab = tabs.find((tab) => tab.index === selectedTab);
    if (currentTab?.disabled) {
      const firstEnabledTab = tabs.find((tab) => !tab.disabled);
      if (firstEnabledTab) {
        changeTab(firstEnabledTab.index);
      }
    }
  }, [tabs, selectedTab, changeTab]);

  const getTabStyles = useCallback(
    (index: number) => {
      if (variant === 'pills') {
        const isTabSelected = selectedTab === index;
        return {
          background: isTabSelected ? 'aliceblue' : 'unset',
          border: `1px solid ${abyssColors.primary[200]}`,
          fontSize: '12px',
          fontWeight: isTabSelected ? 700 : 400,
          borderRadius: 2,
          marginRight: 1,
        };
      }
      return { flex: fullWidth ? 1 : 'unset' };
    },
    [fullWidth, selectedTab, variant]
  );

  return (
    <Stack
      alignItems="stretch"
      direction="column"
      sx={{ height: '100%', position: 'relative', display: hidden ? 'none' : 'flex' }}
    >
      <Box
        sx={{
          borderBottom: variant === 'default' && !customSeperator ? 1 : 0,
          borderColor: 'divider',
          padding: 0,
          marginRight: tabsListRightMargin,
          backgroundColor: tabsBackgroundColor,
        }}
      >
        <Box
          sx={{
            width: '100%',
            position: 'relative',
            '&:after': {
              content: '""',
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              width: '40px',
              background:
                'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 100%)',
              pointerEvents: 'none',
              zIndex: 1,
            },
            '&.scrolled:after': {
              display: 'none',
            },
          }}
        >
          <Box
            sx={{
              width: '100%',
              overflowX: 'auto',
              scrollBehavior: 'smooth',
              '&::-webkit-scrollbar': {
                height: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: `${abyssColors.primary[50]}`,
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: `${abyssColors.primary[100]}`,
                borderRadius: '3px',
                '&:hover': {
                  background: `${abyssColors.primary[200]}`,
                },
              },
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <Tabs
              sx={{
                ...(variant === 'default'
                  ? {
                      '@media (max-width: 1728px)': {
                        minHeight: '36px',
                        '& button': {
                          padding: '6px 16px',
                          minHeight: '36px',
                          minWidth: 'fit-content',
                          whiteSpace: 'nowrap',
                        },
                      },
                      '& button': {
                        textTransform: 'none',
                        fontWeight: 400,
                        whiteSpace: 'nowrap',
                      },
                      '& .Mui-selected': {
                        fontWeight: 500,
                      },
                    }
                  : {}),
                ...tabsStyles,
                display: 'flex',
                width: 'max-content',
                minWidth: '100%',
                '& .MuiTabs-flexContainer': {
                  flexWrap: 'nowrap',
                },
              }}
              value={selectedTab}
              onChange={handleChange}
            >
              {tabs.map((tab, index) => {
                const isSelected = selectedTab === tab.index;
                return (
                  <Tab
                    sx={{
                      ...getTabStyles(index),
                      backgroundColor: tab.disabled ? abyssColors.primary[100] : '#ffffff',
                    }}
                    label={tab.label}
                    key={tab.index}
                    value={tab.index}
                    hidden={isSelected}
                    data-testid={tab?.testid ?? ''}
                    disabled={tab.disabled}
                  />
                );
              })}
            </Tabs>
          </Box>
        </Box>
      </Box>
      {customSeperator && <>{customSeperator}</>}
      {tabs.map((tab) => (
        <TabPanel
          value={selectedTab}
          index={tab.index}
          key={tab.index}
          renderingStrategy={renderingStrategy}
        >
          {tab.component}
        </TabPanel>
      ))}
    </Stack>
  );
}
